import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Card, CardContent, Box, Avatar, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SidebarStore from 'store/ui/sidebarStore';
import domainData from 'store/domainStore';
import WineLoversActivitiesService from 'services/wineLoversActivitiesService';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import { interestsMapping } from 'constants/winlover-filters';
import { adaptWineLoverActivityDetailsData } from 'helpers/adapter';
import { isSuperAdmin } from 'helpers/usersRolesHelper';
import { getMessage } from 'helpers/helper';
import M from 'messages';

import classes from './styles';

const WineLoverActivity = observer(() => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState({});
  const isSidebarOpened = SidebarStore.sidebarState;
  const currentUser = domainData.getCurrentUser();
  const isUserSuperAdmin = isSuperAdmin(currentUser);

  const getActivityData = useCallback(
    async () => {
      try {
        setLoading(true);
        const response = await WineLoversActivitiesService.getWineLoverActivity(id);
        setActivityData(adaptWineLoverActivityDetailsData(response.data));
        setLoading(false);
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), {
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, id],
  );

  useEffect(() => {
    getActivityData();
  }, [getActivityData]);

  const detailsOptions = [
    {
      message: M.get('wineLoversActivities.details.activityType'),
      value: activityData.activity_type,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.activityTimestamp'),
      value: activityData.activity_timestamp,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.partnerName'),
      value: activityData.partner_name,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.producer'),
      value: activityData.producer,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.wineName'),
      value: activityData.wine_name,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.vintage'),
      value: activityData.vintage,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.rating'),
      value: activityData.rating,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.menuName'),
      value: activityData.menu_name,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.userName'),
      value: activityData.user_name,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.userEmail'),
      value: activityData.user_email,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.userId'),
      value: activityData.user_id,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.channel'),
      value: activityData.channel,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.role'),
      value: activityData.role,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.registrationType'),
      value: activityData.registration_type,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.interests'),
      value: activityData.interests,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.country'),
      value: activityData.country,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.state'),
      value: activityData.state,
      show: true,
    },
    {
      message: M.get('wineLoversActivities.details.receiveMarketingEmails'),
      value: activityData.marketing_enabled,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.receiveNewsEmails'),
      value: activityData.receive_news_emails,
      show: isUserSuperAdmin,
    },
    {
      message: M.get('wineLoversActivities.details.receiveEventsEmails'),
      value: activityData.receive_events_emails,
      show: isUserSuperAdmin,
    },
  ];

  if (loading) return <Loading />;

  return (
    <Box
      sx={{
        ...classes.root,
        ...(isSidebarOpened && classes.rootDrawerOpen),
        ...(!isSidebarOpened && classes.rootDrawerClose),
      }}>
      <PageHeader
        generalPageTitle={`${M.get('wineLoversActivities.pageTitle')} | ${activityData.user_name || ''}`}
        onlyGeneralPageTitle
      />
      <Card sx={classes.card}>
        <CardContent classes={{ root: classes.cardContent }}>
          {isUserSuperAdmin && (
            <Box sx={classes.avatar}>
              <Avatar src={activityData.user_image} sx={classes.avatarIcon}>
                {activityData.user_name?.charAt(0)}
              </Avatar>
            </Box>
          )}
          <Box>
            <Grid container sx={classes.detailsContainer}>
              {detailsOptions.map(
                (detail, index) =>
                  detail.show && (
                    <Grid key={index} size={{ xs: 12, sm: 10, md: 9, lg: 7 }} sx={classes.fields}>
                      <Grid size={{ sm: 5, xs: 12 }}>
                        <Typography sx={classes.fieldName}>{detail.message}</Typography>
                      </Grid>
                      <Grid size={{ sm: 7, xs: 12 }}>
                        {detail.message === M.get('wineLoversActivities.details.interests') ? (
                          <Box sx={classes.fieldValue}>
                            {detail.value?.split(',').map(interest => (
                              <Typography key={interest}>
                                {interestsMapping[interest.trim()] || interest.trim()}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography sx={classes.fieldValue}>{detail.value}</Typography>
                        )}
                        <Divider />
                      </Grid>
                    </Grid>
                  ),
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default WineLoverActivity;
